import { createElement, type ReactNode } from "react";
import { dateToISODateOnly, maskCompetencia, maskDateBR } from "@/utils/maskUtils";
import {
	normalizarDataParaISO,
	formatarDataBRParaISO,
	formatarDataISOParaBR,
	isDataISOValida,
} from "@/utils/validators/date";
import { DateDisplayFormat, ISODateOnly } from "./types";

export type CalendarSelectionState = "partial" | "shallow" | "finish";
export type CalendarDateView = "year" | "month" | "day";

export function getCalendarFormatProps(format: DateDisplayFormat) {
	if (format === "mm/yyyy")
		return {
			views: ["year", "month"] as const,
			openTo: "month" as const,
		};

	return {
		views: ["year", "month", "day"] as const,
		openTo: "day" as const,
	};
}

export function isCalendarSelectionFinished(
	format: DateDisplayFormat,
	selectionState?: CalendarSelectionState,
	selectedView?: CalendarDateView,
): boolean {
	if (selectionState) return selectionState === "finish";

	if (selectedView) return selectedView === (format === "mm/yyyy" ? "month" : "day");

	return true;
}

export function dateToISOByDisplayFormat(date: Date, format: DateDisplayFormat): ISODateOnly {
	if (format === "mm/yyyy")
		return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01` as ISODateOnly;

	return dateToISODateOnly(date) as ISODateOnly;
}

export function cmpISO(a: ISODateOnly, b: ISODateOnly): number {
	if (a === b) return 0;
	return a < b ? -1 : 1;
}

export function completeDate(
	incompleteText: string,
	minISO: ISODateOnly | null,
	maxISO: ISODateOnly | null,
): string | null {
	if (!incompleteText || incompleteText.length >= 10) return null;

	const now = new Date();
	const currentMonth = String(now.getMonth() + 1).padStart(2, "0");
	const currentYear = String(now.getFullYear());

	let completedText = incompleteText;

	if (/^\d{1,2}$/.test(incompleteText))
		completedText = `${incompleteText.padStart(2, "0")}/${currentMonth}/${currentYear}`;
	else if (/^\d{1,2}\/\d{1,2}$/.test(incompleteText)) completedText = `${incompleteText}/${currentYear}`;

	const masked = maskDateBR(completedText);
	if (masked.length === 10) {
		const iso = formatarDataBRParaISO(masked);
		if (iso && isDataISOValida(iso)) {
			const isoOnly = iso as ISODateOnly;
			if (minISO && cmpISO(isoOnly, minISO) < 0) return null;
			if (maxISO && cmpISO(isoOnly, maxISO) > 0) return null;
			return masked;
		}
	}
	return null;
}

function asYearMonth(iso: ISODateOnly): `${number}${number}${number}${number}-${number}${number}` {
	return iso.slice(0, 7) as `${number}${number}${number}${number}-${number}${number}`;
}

function isMonthYearText(value: string): boolean {
	if (!/^\d{2}\/\d{4}$/.test(value)) return false;
	const [month] = value.split("/").map(Number);
	return month >= 1 && month <= 12;
}

function preserveMaskedDateOnDeletion(value: string, maxDigits: number, maxSlashes: number): string {
	const sanitized = (value ?? "").replaceAll(/[^\d/]/g, "");
	let digits = 0;
	let slashes = 0;
	let out = "";

	for (const ch of sanitized) {
		if (/\d/.test(ch)) {
			if (digits >= maxDigits) continue;
			out += ch;
			digits += 1;
			continue;
		}

		if (ch === "/") {
			if (slashes >= maxSlashes) continue;
			out += ch;
			slashes += 1;
		}
	}

	return out;
}

function preserveDateSegments(value: string, segmentLengths: readonly number[]): string {
	const maxSlashes = segmentLengths.length - 1;
	let slashes = 0;
	let sanitized = "";

	for (const char of value ?? "") {
		if (/\d/.test(char)) {
			sanitized += char;
			continue;
		}

		if (char === "/" && slashes < maxSlashes) {
			sanitized += char;
			slashes += 1;
		}
	}

	return sanitized
		.split("/")
		.map((segment, index) => segment.slice(0, segmentLengths[index] ?? 0))
		.join("/");
}

export function maskDateByFormat(value: string, format: DateDisplayFormat, previousValue = ""): string {
	const segmentLengths = format === "mm/yyyy" ? [2, 4] : [2, 2, 4];
	const expectedSlashes = segmentLengths.length - 1;
	const slashCount = [...(value ?? "")].filter((char) => char === "/").length;

	if (slashCount >= expectedSlashes) return preserveDateSegments(value, segmentLengths);

	const isDeletingMaskedContent = value.length < previousValue.length;

	if (isDeletingMaskedContent) {
		if (format === "mm/yyyy") return preserveMaskedDateOnDeletion(value, 6, 1);

		return preserveMaskedDateOnDeletion(value, 8, 2);
	}

	if (format === "mm/yyyy") return maskCompetencia(value);
	return maskDateBR(value);
}

export function displayDateToISO(value: string, format: DateDisplayFormat): ISODateOnly | null {
	if (format === "mm/yyyy") {
		if (!isMonthYearText(value)) return null;
		const [month, year] = value.split("/");
		return `${year}-${month}-01` as ISODateOnly;
	}

	const iso = formatarDataBRParaISO(value);
	if (!iso || !isDataISOValida(iso)) return null;
	return iso as ISODateOnly;
}

export function isoToDisplayDate(iso: ISODateOnly | null, format: DateDisplayFormat): string {
	if (!iso) return "";
	if (format === "mm/yyyy") {
		const [year, month] = iso.split("-");
		return `${month}/${year}`;
	}
	return formatarDataISOParaBR(iso) ?? "";
}

export function completeDisplayDate(
	incompleteText: string,
	format: DateDisplayFormat,
	minISO: ISODateOnly | null,
	maxISO: ISODateOnly | null,
): string | null {
	if (format === "dd/mm/yyyy") return completeDate(incompleteText, minISO, maxISO);

	if (!incompleteText || incompleteText.length >= 7) return null;

	const now = new Date();
	const currentYear = String(now.getFullYear());

	let completedText = incompleteText;

	if (/^\d{1,2}$/.test(incompleteText)) completedText = `${incompleteText.padStart(2, "0")}/${currentYear}`;

	const masked = maskCompetencia(completedText);
	if (masked.length !== 7) return null;

	const iso = displayDateToISO(masked, "mm/yyyy");
	if (!iso) return null;

	const yearMonth = asYearMonth(iso);
	if (minISO && yearMonth < asYearMonth(minISO)) return null;
	if (maxISO && yearMonth > asYearMonth(maxISO)) return null;

	return masked;
}

export function isISOWithinBounds(
	iso: ISODateOnly,
	minISO: ISODateOnly | null,
	maxISO: ISODateOnly | null,
	format: DateDisplayFormat,
): "minDate" | "maxDate" | null {
	if (format === "mm/yyyy") {
		const yearMonth = asYearMonth(iso);
		if (minISO && yearMonth < asYearMonth(minISO)) return "minDate";
		if (maxISO && yearMonth > asYearMonth(maxISO)) return "maxDate";
		return null;
	}

	if (minISO && cmpISO(iso, minISO) < 0) return "minDate";
	if (maxISO && cmpISO(iso, maxISO) > 0) return "maxDate";
	return null;
}

export function pickerErrorToMessage(
	err: unknown,
	minMessage = "Data menor que o mínimo permitido",
	maxMessage = "Data maior que o máximo permitido",
): string | null {
	switch (err) {
		case "invalidDate":
			return "Data inválida";
		case "minDate":
			return minMessage;
		case "maxDate":
			return maxMessage;
		default:
			return null;
	}
}

export function toISOOrNull(value: unknown): ISODateOnly | null {
	const iso = normalizarDataParaISO(value);
	if (!iso || !isDataISOValida(iso)) return null;
	return iso as ISODateOnly;
}

export function withRequiredAsterisk(label: ReactNode, required?: boolean, includeText = false): ReactNode {
	if (!required) return label;
	const asterisk = createElement("span", { className: "text-danger" }, "*");
	const extra = includeText && createElement("span", { className: "text-danger ml-1 text-sm" }, "(Obrigatório)");

	if (label == null || label === "")
		// caso não haja label, mostramos apenas o texto/asterisco
		return createElement("span", { className: "inline-flex items-center gap-0.5" }, extra, asterisk);

	return createElement("span", { className: "inline-flex items-center gap-0.5" }, label, asterisk, extra);
}
